/**
 * Dashboard Page
 *
 * Enterprise features:
 * - Real-time stats with configurable polling
 * - Parallel API fetching with error isolation
 * - Stale-while-revalidate pattern
 * - Skeleton loading states
 * - Auto-refresh capability
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { adminApi, tenantsApi, activityApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery } from '../../hooks/useDataFetch';
import { useActivityStream } from '../../hooks/useRealtime';
import './Dashboard.css';

// Constants
const STATS_REFRESH_INTERVAL = 30000; // 30 seconds
const ACTIVITY_REFRESH_INTERVAL = 15000; // 15 seconds

const Dashboard = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [recentActivity, setRecentActivity] = useState([]);

  // Fetch dashboard stats with polling
  const {
    data: dashboardData,
    isLoading: loadingStats,
    error: statsError,
    refetch: refetchStats,
  } = useQuery(
    () => adminApi.dashboard(),
    {
      key: 'dashboard:stats',
      cacheTTL: 20000,
      refetchInterval: STATS_REFRESH_INTERVAL,
      onError: () => {
        // Silent fail for stats - show cached data
      },
    }
  );

  // Fetch top tenants
  const {
    data: tenantsData,
    isLoading: loadingTenants,
  } = useQuery(
    () => tenantsApi.list({ limit: 5, sort: 'users_count:desc' }),
    {
      key: 'dashboard:topTenants',
      cacheTTL: 60000,
      onError: () => {
        // Silent fail
      },
    }
  );

  // Fetch recent activity with polling
  const {
    data: activityData,
    isLoading: loadingActivity,
    refetch: refetchActivity,
  } = useQuery(
    () => activityApi.list({ limit: 5 }),
    {
      key: 'dashboard:activity',
      cacheTTL: 10000,
      refetchInterval: ACTIVITY_REFRESH_INTERVAL,
      onSuccess: (data) => {
        const logs = data.logs || data.data || data.items || [];
        setRecentActivity(logs);
      },
      onError: () => {
        // Silent fail
      },
    }
  );

  // Real-time activity updates
  useActivityStream((event) => {
    if (event) {
      setRecentActivity(prev => [event, ...prev.slice(0, 4)]);
    }
  });

  // Compute stats from dashboard data
  const stats = useMemo(() => {
    const data = dashboardData || {};
    return [
      {
        label: 'Total Tenants',
        value: formatNumber(data.totalTenants || data.companies || 0),
        change: formatPercent(data.tenantsGrowth),
        changeType: getChangeType(data.tenantsGrowth),
        icon: 'building',
      },
      {
        label: 'Active Users',
        value: formatNumber(data.activeUsers || data.users || 0),
        change: formatPercent(data.usersGrowth),
        changeType: getChangeType(data.usersGrowth),
        icon: 'users',
      },
      {
        label: 'MRR',
        value: formatCurrency(data.mrr || data.revenue || 0),
        change: formatPercent(data.mrrGrowth || data.revenueGrowth),
        changeType: getChangeType(data.mrrGrowth || data.revenueGrowth),
        icon: 'dollar-sign',
      },
      {
        label: 'Fraud Blocked',
        value: formatNumber(data.fraudBlocked || data.securityEvents || 0),
        change: data.fraudGrowth ? `${data.fraudGrowth > 0 ? '+' : ''}${data.fraudGrowth}%` : undefined,
        changeType: data.fraudGrowth > 0 ? 'warning' : 'positive',
        icon: 'shield',
      },
    ];
  }, [dashboardData]);

  // Extract tenants
  const topTenants = useMemo(() => {
    const data = tenantsData || {};
    return data.companies || data.data || data.items || [];
  }, [tenantsData]);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetchStats();
    refetchActivity();
    showToast('Dashboard refreshed', 'success');
  }, [refetchStats, refetchActivity, showToast]);

  // Helpers
  function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K`;
    return num.toLocaleString();
  }

  function formatCurrency(amount) {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  function formatPercent(value) {
    if (value === null || value === undefined) return undefined;
    return `${value > 0 ? '+' : ''}${Math.abs(value).toFixed(1)}%`;
  }

  function getChangeType(value) {
    if (value === null || value === undefined) return 'neutral';
    return value >= 0 ? 'positive' : 'negative';
  }

  function getActivityIcon(type) {
    const icons = {
      login: { icon: 'log-in', className: 'info' },
      user_login: { icon: 'log-in', className: 'info' },
      logout: { icon: 'log-out', className: 'info' },
      fraud: { icon: 'alert-triangle', className: 'warning' },
      security: { icon: 'shield', className: 'warning' },
      blocked: { icon: 'x-circle', className: 'danger' },
      tenant: { icon: 'building', className: 'success' },
      company_created: { icon: 'check-circle', className: 'success' },
      user_created: { icon: 'user-plus', className: 'success' },
      upgrade: { icon: 'trending-up', className: 'success' },
      subscription: { icon: 'credit-card', className: 'info' },
    };
    return icons[type?.toLowerCase()] || { icon: 'activity', className: 'info' };
  }

  function formatTimeAgo(timestamp) {
    if (!timestamp) return '';
    const seconds = Math.floor((Date.now() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  }

  const isLoading = loadingStats && !dashboardData;

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Welcome back! Here's what's happening.</p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="refresh"
            onClick={handleRefresh}
            disabled={loadingStats}
          >
            {loadingStats ? 'Syncing...' : 'Refresh'}
          </Button>
          <Button
            variant="primary"
            icon="plus"
            onClick={() => navigate('/tenants')}
          >
            New Tenant
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {statsError && (
        <div className="dashboard-error">
          <Icon name="alert-triangle" size="sm" />
          <span>Failed to load some data. Showing cached information.</span>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>Retry</Button>
        </div>
      )}

      {/* Stats Row */}
      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={isLoading} />
        ))}
      </div>

      {/* Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Recent Activity */}
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">Recent Activity</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/activity')}
            >
              View All
            </Button>
          </div>
          <div className="activity-list">
            {loadingActivity && recentActivity.length === 0 ? (
              <div className="activity-loading">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="activity-item skeleton">
                    <div className="skeleton-icon"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-line"></div>
                      <div className="skeleton-line short"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentActivity.length > 0 ? (
              recentActivity.slice(0, 5).map((item, index) => {
                const { icon, className } = getActivityIcon(item.action || item.type);
                return (
                  <div key={item.id || index} className="activity-item">
                    <div className={`activity-icon ${className}`}>
                      <Icon name={icon} size="sm" />
                    </div>
                    <div className="activity-content">
                      <div className="activity-title">
                        {item.description || item.action || item.message}
                      </div>
                      <div className="activity-meta">
                        {item.user?.email || item.actor || item.userEmail || 'System'}
                        {' • '}
                        {formatTimeAgo(item.createdAt || item.timestamp)}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="activity-empty">
                <Icon name="activity" size="lg" />
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </section>

        {/* Top Tenants */}
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">Top Tenants</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/tenants')}
            >
              View All
            </Button>
          </div>
          <div className="tenant-list">
            {loadingTenants && topTenants.length === 0 ? (
              <div className="tenant-loading">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="tenant-row skeleton">
                    <div className="skeleton-content">
                      <div className="skeleton-line"></div>
                      <div className="skeleton-line short"></div>
                    </div>
                    <div className="skeleton-badge"></div>
                  </div>
                ))}
              </div>
            ) : topTenants.length > 0 ? (
              topTenants.slice(0, 5).map((tenant) => (
                <div
                  key={tenant.id}
                  className="tenant-row"
                  onClick={() => navigate('/tenants')}
                >
                  <div className="tenant-info">
                    <div className="tenant-name">{tenant.name}</div>
                    <div className="tenant-domain">
                      {tenant.domain || tenant.subdomain || '-'}
                    </div>
                  </div>
                  <div className="tenant-stats">
                    <span className="tenant-users">
                      {formatNumber(tenant.usersCount || tenant.users_count || 0)} users
                    </span>
                    <span className={`tenant-badge ${tenant.status === 'active' ? 'active' : 'inactive'}`}>
                      {tenant.status || 'Active'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="tenant-empty">
                <Icon name="building" size="lg" />
                <p>No tenants yet</p>
                <Button
                  variant="primary"
                  size="sm"
                  icon="plus"
                  onClick={() => navigate('/tenants')}
                >
                  Add Tenant
                </Button>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
